import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { supabase, authHelpers } from './supabaseClient';
import { identifyUserWithLogRocket, logRocketEvents } from './utils/logRocket';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEligibility, setTestEligibility] = useState(null);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  
  // Use ref to track loading state and prevent stale closures
  const loadingRef = useRef(false);
  const profileLoadedRef = useRef(false);  // Track if profile has been loaded

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          profileLoadedRef.current = true;  // Mark as loaded for initial session
          await loadUserProfile(session.user);
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        if (session?.user && event !== 'TOKEN_REFRESHED') {
          setUser(session.user);
          // ONLY load profile once per session to prevent infinite loops
          if ((event === 'SIGNED_IN' || event === 'SIGNED_UP') && !profileLoadedRef.current) {
            profileLoadedRef.current = true;
            await loadUserProfile(session.user);
          }
          // For INITIAL_SESSION and subsequent events, don't reload profile
        } else {
          setUser(null);
          setUserProfile(null);
          setTestEligibility(null);
          profileLoadedRef.current = false;  // Reset on logout
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []); // Remove isLoadingProfile dependency to prevent circular calls

  const loadUserProfile = useCallback(async (authUser) => {
    // Use a ref to track loading state to avoid stale closures
    if (loadingRef.current) {
      console.log('Profile already loading, skipping...');
      return;
    }
    
    try {
      loadingRef.current = true;
      setIsLoadingProfile(true);
      console.log('Loading user profile for:', authUser.id, authUser.email);
      
      // Update user profile in our database
      const profileData = {
        user_id: authUser.id,
        email: authUser.email,
        full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
        grade: authUser.user_metadata?.grade, // Include grade from user metadata
        parent_email: authUser.user_metadata?.parent_email // Include parent_email from user metadata
      };
      
      console.log('🔍 Updating profile with data:', profileData);
      const profileResult = await authHelpers.updateUserProfile(profileData);

      console.log('🔍 Profile update result:', profileResult);
      console.log('🔍 Profile update success:', profileResult?.success);
      console.log('🔍 Profile update error:', profileResult?.error);

      if (profileResult?.success && profileResult?.user) {
        setUserProfile(profileResult.user);

        // Identify user with LogRocket now that we have complete profile data
        try {
          identifyUserWithLogRocket(authUser, profileResult.user);
        } catch (error) {
          console.error('Error identifying user with LogRocket:', error);
        }

        // Check if grade is missing for legacy users (non-admin)
        // Only check database profile, not user_metadata to avoid showing modal repeatedly
        console.log('=== GRADE CHECK DEBUG ===');
        console.log('User role:', profileResult.user?.role);
        console.log('User grade from profile:', profileResult.user?.grade);
        console.log('User metadata grade:', authUser.user_metadata?.grade);
        console.log('Current showGradeModal state:', showGradeModal);

        if (profileResult.user?.role !== 'admin' && !profileResult.user?.grade) {
          console.log('Grade missing for user, showing grade collection modal');
          setShowGradeModal(true);
        } else {
          // Ensure grade modal is closed for users with grade
          console.log('User has grade or is admin, closing grade modal');
          setShowGradeModal(false);
        }
        console.log('=== END GRADE CHECK DEBUG ===');

        // Load test eligibility
        const eligibility = await authHelpers.checkTestEligibility(authUser.id);
        console.log('Test eligibility result:', eligibility);
        setTestEligibility(eligibility);
      } else {
        console.error('Failed to update user profile:', profileResult.error || profileResult.detail || profileResult);
        // Set fallback user profile from auth user data when database update fails
        const fallbackProfile = {
          user_id: authUser.id,
          email: authUser.email,
          full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
          grade: authUser.user_metadata?.grade,
          parent_email: authUser.user_metadata?.parent_email,
          role: 'student'
        };
        setUserProfile(fallbackProfile);
        
        // Set default eligibility for new users if profile creation fails
        setTestEligibility({
          can_take_test: true,
          tests_taken: 0,
          remaining_tests: 3,
          role: 'student',
          subscription_tier: 'free',
          message: 'Welcome! You have 3 free tests to get started.'
        });
        
        // Show grade modal if grade is missing
        if (!authUser.user_metadata?.grade) {
          setShowGradeModal(true);
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      // Set fallback user profile from auth user data when there's an error
      const fallbackProfile = {
        user_id: authUser.id,
        email: authUser.email,
        full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
        grade: authUser.user_metadata?.grade,
        parent_email: authUser.user_metadata?.parent_email,
        role: 'student'
      };
      setUserProfile(fallbackProfile);
      
      // Set default eligibility for new users if there's an error
      setTestEligibility({
        can_take_test: true,
        tests_taken: 0,
        remaining_tests: 3,
        role: 'student',
        subscription_tier: 'free',
        message: 'Welcome! You have 3 free tests to get started.'
      });
      
      // Show grade modal if grade is missing
      if (!authUser.user_metadata?.grade) {
        setShowGradeModal(true);
      }
    } finally {
      loadingRef.current = false;
      setIsLoadingProfile(false);
    }
  }, []); // Use ref instead of state dependencies to prevent circular calls

  const signUp = async (email, password, fullName, grade, parentEmail) => {
    setLoading(true);
    try {
      const result = await authHelpers.signUp(email, password, fullName, grade, parentEmail);
      if (result.success) {
        // Track sign up event with LogRocket
        logRocketEvents.userSignUp({
          email,
          fullName,
          grade,
          hasParentEmail: !!parentEmail
        });

        // Profile will be loaded automatically via auth state change
        return { success: true, message: 'Please check your email to confirm your account.' };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      const result = await authHelpers.signIn(email, password);
      if (result.success) {
        // Profile will be loaded automatically via auth state change
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      // Track sign out event with LogRocket
      logRocketEvents.userSignOut();

      const result = await authHelpers.signOut();
      if (result.success) {
        setUser(null);
        setUserProfile(null);
        setTestEligibility(null);
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      // Track password reset event with LogRocket
      logRocketEvents.passwordReset(email);

      const result = await authHelpers.resetPassword(email);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const refreshTestEligibility = async () => {
    if (user) {
      const eligibility = await authHelpers.checkTestEligibility(user.id);
      setTestEligibility(eligibility);
      return eligibility;
    }
    return null;
  };

  const updateUserProfile = async (updateData) => {
    try {
      const result = await authHelpers.updateUserProfile(updateData);
      if (result.success && user) {
        // Also update Supabase user metadata to persist grade info
        if (updateData.grade) {
          try {
            await supabase.auth.updateUser({
              data: { 
                grade: updateData.grade,
                full_name: updateData.full_name 
              }
            });
            console.log('Updated Supabase user metadata with grade:', updateData.grade);
          } catch (metaError) {
            console.error('Failed to update user metadata:', metaError);
            // Don't fail the whole operation if metadata update fails
          }
        }
        
        // Update local state directly instead of reloading entire profile
        setUserProfile(result.user);
        
        // Close grade modal if it was open
        setShowGradeModal(false);
        
        // Only update test eligibility if needed
        if (updateData.grade) {
          const eligibility = await authHelpers.checkTestEligibility(user.id);
          setTestEligibility(eligibility);
        }
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const closeGradeModal = () => {
    setShowGradeModal(false);
  };

  // Memoize callback functions to prevent re-creation on every render
  const signUpCallback = useCallback(signUp, []);
  const signInCallback = useCallback(signIn, []);
  const signOutCallback = useCallback(signOut, []);
  const resetPasswordCallback = useCallback(resetPassword, []);
  const refreshTestEligibilityCallback = useCallback(refreshTestEligibility, [user?.id]);
  const updateUserProfileCallback = useCallback(updateUserProfile, []);
  const closeGradeModalCallback = useCallback(closeGradeModal, []);

  // Memoize the context value to prevent infinite re-renders
  const value = useMemo(() => ({
    user,
    userProfile,
    testEligibility,
    loading,
    showGradeModal,
    signUp: signUpCallback,
    signIn: signInCallback,
    signOut: signOutCallback,
    resetPassword: resetPasswordCallback,
    refreshTestEligibility: refreshTestEligibilityCallback,
    updateUserProfile: updateUserProfileCallback,
    closeGradeModal: closeGradeModalCallback,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'admin',
    hasRequiredProfile: !!userProfile?.grade || userProfile?.role === 'admin'
  }), [
    user,
    userProfile,
    testEligibility,
    loading,
    showGradeModal,
    signUpCallback,
    signInCallback,
    signOutCallback,
    resetPasswordCallback,
    refreshTestEligibilityCallback,
    updateUserProfileCallback,
    closeGradeModalCallback
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;